import { pxToEm } from 'util/pxToEm';
import React, { ReactElement } from 'react';
import { Event, OrganizationSummary } from 'services/api';
import { Link } from 'components/lib';
import { Box } from '@mui/material';
import { Routes } from 'services/routes';
import { uniqBy } from 'lodash';
import { SchoolSummary } from 'services/api/models/school';

interface HashTagsProps {
  sampledEvents: Event[];
  allEvents: Event[];
}

export interface CreateUniqEventsProps {
  allCategories: string[];
  allOrg: OrganizationSummary[];
  school: SchoolSummary | null;
}

export const HashTags = ({
  sampledEvents = [],
  allEvents = [],
}: HashTagsProps): ReactElement | null => {
  const createUniqEvents = (events: Event[]): CreateUniqEventsProps => {
    let allCategories: string[] = [];
    let allOrg: OrganizationSummary[] = [];
    let school: SchoolSummary | null = null;

    events.forEach(event => {
      allCategories = [...allCategories, ...event.categories];
      allOrg = [...allOrg, ...event.hostOrganizations];
      school = event.school;
    });

    return {
      allCategories: [...new Set(allCategories)],
      allOrg: uniqBy(allOrg, 'id'),
      school,
    };
  };

  const uniqSampledEvents = createUniqEvents(sampledEvents);
  const uniqAllEvents = createUniqEvents(allEvents);

  const createLabel = (key: string, text: string, slug = '', params = '') => (
    <Box key={key} mb={{ xs: 40, md: 32 }}>
      <Link
        href={`${Routes.explore(slug)}${params}`}
        sx={{
          fontSize: pxToEm(14),
          fontWeight: 400,
          marginRight: 40,

          color: theme => theme.palette.text.primary,
          '&:hover': {
            textDecoration: 'underline',
          },
        }}
      >{`# ${text}`}</Link>
    </Box>
  );

  if (
    !uniqSampledEvents?.school &&
    !uniqSampledEvents.allOrg &&
    !uniqAllEvents?.allCategories
  )
    return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        width: '100%',
        marginTop: 24,
        marginBottom: 24,
      }}
    >
      <Box display={{ xs: 'unset', md: 'flex' }}>
        {uniqSampledEvents?.school && (
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }}>
            {createLabel(
              uniqSampledEvents.school.id,
              uniqSampledEvents.school.name,
              uniqSampledEvents.school.slug,
            )}
          </Box>
        )}
        {uniqSampledEvents.allOrg && (
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }}>
            {uniqSampledEvents.allOrg.map(org =>
              createLabel(
                org.id,
                org.name,
                uniqSampledEvents?.school?.slug,
                `?organization=${org.id}`,
              ),
            )}
          </Box>
        )}
      </Box>

      {uniqAllEvents?.allCategories && (
        <Box
          display="flex"
          flexWrap="wrap"
          flexDirection={{ xs: 'column', md: 'row' }}
        >
          {uniqAllEvents.allCategories.map(category =>
            createLabel(
              category,
              category,
              uniqAllEvents?.school?.slug,
              `?categories=${encodeURIComponent(category)}`,
            ),
          )}
        </Box>
      )}
    </Box>
  );
};
